import { useState } from "react";
import { AlertCircle, CheckCircle2, ImagePlus, Loader2, RotateCcw, X } from "lucide-react";

const ACCEPT = "image/png,image/jpeg,image/heic,image/heif";

const Thumb = ({ url, index }) => {
  const [broken, setBroken] = useState(false);
  if (broken) return <span className="flex aspect-[9/16] w-full items-center justify-center p-2 text-center text-[11px] text-slate-500">Preview not available in this browser</span>;
  return <img src={url} alt={`Screenshot ${index + 1}`} onError={() => setBroken(true)} className="aspect-[9/16] w-full object-cover object-top" />;
};

const Status = ({ shot }) => {
  if (shot.status === "extracting") return <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Reading…</span>;
  if (shot.status === "done") return <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> {shot.rows} rows</span>;
  if (shot.status === "error") return <span className="flex items-center gap-1 text-[11px] font-semibold text-red-600"><AlertCircle className="h-3 w-3" /> Failed</span>;
  return <span className="text-[11px] text-slate-400">Waiting</span>;
};

export const ScreenshotStrip = ({ shots, onPick, onView, onRetry, onRemove, disabled }) => (
  <div data-testid="import-shots">
    <label className={`flex min-h-[56px] cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50 px-4 text-base font-semibold text-blue-700 ${disabled ? "pointer-events-none opacity-50" : "hover:bg-blue-100"}`}>
      <ImagePlus className="h-5 w-5" /> Choose screenshots
      <input data-testid="import-file-input" type="file" accept={ACCEPT} multiple className="sr-only" disabled={disabled}
        onChange={(e) => { onPick([...e.target.files]); e.target.value = ""; }} />
    </label>
    <p className="mt-2 text-xs text-slate-500">PNG or JPEG, up to 15 screenshots, 10MB each. Screenshots stay on this device and are only sent to be read.</p>
    {shots.length > 0 && (
      <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5">
        {shots.map((s, i) => (
          <li key={s.key} data-testid={`import-shot-${i}`} className="relative">
            <button type="button" data-testid={`import-shot-view-${i}`} onClick={() => onView(i)} className="block w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
              <Thumb url={s.url} index={i} />
            </button>
            <button type="button" aria-label="Remove screenshot" data-testid={`import-shot-remove-${i}`} disabled={s.status === "extracting"} onClick={() => onRemove(i)} className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white text-slate-500 shadow ring-1 ring-slate-200 disabled:opacity-40"><X className="h-3.5 w-3.5" /></button>
            <p className="mt-1 text-[11px] font-bold text-slate-700">Screenshot {i + 1}</p>
            <Status shot={s} />
            {s.status === "error" && (
              <div data-testid={`import-shot-error-${i}`} className="mt-1 space-y-1">
                <p className="text-[11px] leading-snug text-red-600">{s.error}</p>
                <button type="button" data-testid={`import-shot-retry-${i}`} onClick={() => onRetry(i)} className="inline-flex min-h-[32px] items-center gap-1 rounded-lg bg-red-600 px-2 text-[11px] font-semibold text-white"><RotateCcw className="h-3 w-3" /> Retry</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const ScreenshotViewer = ({ shot, index, onClose }) => {
  if (!shot) return null;
  return (
    <div data-testid="import-shot-viewer" role="dialog" aria-modal="true" aria-label={`Screenshot ${index + 1}`} className="fixed inset-0 z-50 flex flex-col bg-slate-950/95">
      <div className="flex items-center justify-between px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))] text-white">
        <p className="text-sm font-semibold">Screenshot {index + 1}</p>
        <button type="button" data-testid="import-shot-viewer-close" onClick={onClose} aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 overflow-auto px-2 pb-6">
        <img src={shot.url} alt={`Screenshot ${index + 1} full size`} className="mx-auto w-full max-w-md" />
      </div>
    </div>
  );
};
